// client/src/hooks/useReports.js
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../services/api';
import { useAuth } from '../context/auth/AuthProvider'; // ✅ ADICIONE ESTE IMPORT
import { format } from 'date-fns';

const REPORTS_QUERY_KEY = 'reports';

// 🔥 CONSTANTES PARA CONFIGURAÇÃO
const REPORT_TYPES = {
  DASHBOARD_CONTA: 'dashboard/conta',
  DASHBOARD_MATRIZ: 'dashboard/matriz',
  PLAN_USAGE: 'plan-usage',
  VENDAS_CONSOLIDADO: 'vendas/consolidado',
  VENDAS_COMISSOES: 'vendas/comissoes',
  VENDAS_PRODUTOS: 'vendas/produtos',
  VENDAS_EVOLUCAO: 'vendas/evolucao',
  FATURAMENTO: 'indicadores/faturamento',
  CLIENTES: 'indicadores/clientes',
  AGENDAMENTOS: 'indicadores/agendamentos',
  PAGAMENTOS: 'indicadores/pagamentos',
  ANIVERSARIANTES: 'indicadores/aniversariantes',
  COMISSOES: 'indicadores/comissoes',
  PRODUTOS: 'indicadores/produtos',
  SERVICOS: 'indicadores/servicos'
};

// 🔥 FUNÇÃO AUXILIAR: Limpar e formatar filtros (CORRIGIDA)
const cleanAndFormatFilters = (filters = {}) => {
  // ✅ CORREÇÃO: Verificar se filters é null ou undefined
  if (!filters || typeof filters !== 'object') {
    return {};
  }
  
  const cleanFilters = Object.fromEntries(
    Object.entries(filters).filter(([_, v]) => v != null && v !== '')
  );

  // ✅ CORREÇÃO: Formatar datas ISO para garantir compatibilidade com timezone
  if (cleanFilters.startDate) {
    const date = new Date(cleanFilters.startDate);
    cleanFilters.startDate = date.toISOString().split('T')[0]; // Formato YYYY-MM-DD
  }
  if (cleanFilters.endDate) {
    const date = new Date(cleanFilters.endDate);
    cleanFilters.endDate = date.toISOString().split('T')[0]; // Formato YYYY-MM-DD
  }

  return cleanFilters;
};

// 🔥 FUNÇÃO AUXILIAR: Log consistente
const logReport = (tipo, params = {}) => {
  console.log(`📊 Buscando relatório ${tipo}:`, params);
};

// 🔥 FUNÇÃO AUXILIAR: Tratamento de erro consistente
const handleReportError = (tipo, error, defaultValue = null) => {
  console.error(`❌ Erro ao buscar relatório ${tipo}:`, error);
  return defaultValue;
};

// ===================================================================================
// HOOKS PARA DASHBOARDS
// ===================================================================================

/**
 * Hook para dashboard da conta
 */
export const useContaDashboard = (filters = {}) => {
  const { user, scope } = useAuth();
  const queryParams = cleanAndFormatFilters(filters);

  // ✅ CORREÇÃO: backend agora soma os dados de toda a franquia quando o
  // usuário não tem `.account` mas tem `.franquia`, e os dados de TODAS as
  // contas/franquias quando é matriz (getContaDashboard usa
  // getAccountFranchiseIds) — a guarda/queryKey precisam reconhecer os
  // três casos, senão franquia_admin/matriz_admin nunca disparam essa
  // query e veem o dashboard sempre zerado.
  const tenantId = user?.account?._id || user?.franquia?._id;
  const isMatriz = scope === 'matriz';

  return useQuery({
    queryKey: [REPORTS_QUERY_KEY, 'dashboard', 'conta', queryParams, tenantId, isMatriz],
    queryFn: async () => {
      try {
        logReport('Dashboard Conta', queryParams);

        const response = await apiClient.get('/reports/dashboard/conta', {
          params: queryParams
        });

        console.log('✅ Dashboard conta carregado:', response.data.data);
        return response.data.data;
      } catch (error) {
        return handleReportError('dashboard conta', error, {
          kpis: {
            totalAgendamentos: 0,
            faturamentoTotal: 0,
            agendamentosPagos: 0,
            novosClientes: 0,
            ticketMedio: 0
          },
          evolucaoDiaria: [],
          evolucaoMensal: [],
          evolucaoAnual: []
        });
      }
    },
    // ⚠️ Sem essa guarda, um usuário sem conta/franquia/matriz disparava
    // essa requisição sem tenant, que travava até o timeout de 30s do axios.
    enabled: !!tenantId || isMatriz,
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
  });
};

/**
 * Hook para dashboard da matriz
 */
export const useMatrizDashboard = (filters = {}) => {
  const queryParams = cleanAndFormatFilters(filters);

  return useQuery({
    queryKey: [REPORTS_QUERY_KEY, 'dashboard', 'matriz', queryParams],
    queryFn: async () => {
      try {
        logReport('Dashboard Matriz', queryParams);
        
        const response = await apiClient.get('/reports/dashboard/matriz', {
          params: queryParams
        });
        
        console.log('✅ Dashboard matriz carregado:', response.data.data);
        return response.data.data;
      } catch (error) {
        return handleReportError('dashboard matriz', error, {
          periodo: { startDate: '', endDate: '' },
          totalAssinaturas: 0,
          ativas: 0,
          trial: 0,
          canceladas: 0,
          inadimplentes: 0,
          totalMRR: 0,
          novasContas: 0
        });
      }
    },
    staleTime: 10 * 60 * 1000,
    cacheTime: 15 * 60 * 1000,
  });
};

/**
 * Hook para relatório de uso do plano
 */
export const usePlanUsageReport = () => {
  return useQuery({
    queryKey: [REPORTS_QUERY_KEY, 'plan-usage'],
    queryFn: async () => {
      try {
        logReport('Uso do Plano');
        
        const response = await apiClient.get('/reports/plan-usage');
        
        console.log('✅ Relatório de uso do plano carregado:', response.data.data);
        return response.data.data;
      } catch (error) {
        return handleReportError('uso do plano', error, {
          plano: 'Plano não carregado',
          relatorioUso: []
        });
      }
    },
    staleTime: 15 * 60 * 1000,
    cacheTime: 30 * 60 * 1000,
  });
};

// ===================================================================================
// HOOKS PARA RELATÓRIOS DE VENDAS
// ===================================================================================

/**
 * Hook para relatório consolidado de vendas
 */
export const useVendasConsolidado = (filters = {}) => {
  const queryParams = cleanAndFormatFilters(filters);

  return useQuery({
    queryKey: [REPORTS_QUERY_KEY, 'vendas', 'consolidado', queryParams],
    queryFn: async () => {
      try {
        logReport('Vendas Consolidado', queryParams);
        
        const response = await apiClient.get('/reports/vendas/consolidado', {
          params: queryParams
        });
        
        console.log('✅ Relatório consolidado de vendas carregado:', response.data.data);
        return response.data.data;
      } catch (error) {
        return handleReportError('vendas consolidado', error, {
          totalVendas: 0,
          totalFaturado: 0,
          totalDescontos: 0,
          totalPago: 0,
          saldoDevedor: 0,
          mediaTicket: 0,
          vendasFinalizadas: 0,
          vendasCanceladas: 0,
          taxaConversao: 0
        });
      }
    },
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
  });
};

/**
 * Hook para relatório de comissões de vendas
 */
export const useVendasComissoes = (filters = {}) => {
  const queryParams = cleanAndFormatFilters(filters);

  return useQuery({
    queryKey: [REPORTS_QUERY_KEY, 'vendas', 'comissoes', queryParams],
    queryFn: async () => {
      try {
        logReport('Comissões de Vendas', queryParams);
        
        const response = await apiClient.get('/reports/vendas/comissoes', {
          params: queryParams
        });
        
        const result = response.data.data || { comissoes: [], estatisticasGerais: {} };
        console.log('✅ Relatório de comissões carregado:', result.comissoes?.length || 0, 'itens');
        return result;
      } catch (error) {
        return handleReportError('comissões de vendas', error, {
          comissoes: [],
          estatisticasGerais: {
            totalComissoes: 0,
            totalVendas: 0,
            totalProfissionais: 0
          }
        });
      }
    },
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
  });
};

/**
 * Hook para relatório de vendas por produto
 */
export const useVendasPorProduto = (filters = {}) => {
  const queryParams = cleanAndFormatFilters(filters);

  return useQuery({
    queryKey: [REPORTS_QUERY_KEY, 'vendas', 'produtos', queryParams],
    queryFn: async () => {
      try {
        logReport('Vendas por Produto', queryParams);
        
        const response = await apiClient.get('/reports/vendas/produtos', {
          params: queryParams
        });
        
        const result = response.data.data || [];
        console.log('✅ Vendas por produto carregadas:', result.length, 'produtos');
        return result;
      } catch (error) {
        return handleReportError('vendas por produto', error, []);
      }
    },
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
  });
};

/**
 * Hook para evolução temporal de vendas
 */
export const useEvolucaoVendas = (filters = {}) => {
  const queryParams = cleanAndFormatFilters(filters);

  return useQuery({
    queryKey: [REPORTS_QUERY_KEY, 'vendas', 'evolucao', queryParams],
    queryFn: async () => {
      try {
        logReport('Evolução de Vendas', queryParams);
        
        const response = await apiClient.get('/reports/vendas/evolucao', {
          params: queryParams
        });
        
        const result = response.data.data || [];
        console.log('✅ Evolução de vendas carregada:', result.length, 'períodos');
        return result;
      } catch (error) {
        return handleReportError('evolução de vendas', error, []);
      }
    },
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
  });
};

// ===================================================================================
// HOOKS PARA RELATÓRIOS DETALHADOS (INDICADORES) - CORRIGIDOS
// ===================================================================================

/**
 * Hook genérico para relatórios de indicadores (CORRIGIDO)
 */
const useGenericReport = (endpoint, filters = {}, options = {}) => {
  const queryParams = cleanAndFormatFilters(filters);

  return useQuery({
    queryKey: [REPORTS_QUERY_KEY, 'indicadores', endpoint, queryParams],
    queryFn: async () => {
      try {
        logReport(`Indicador ${endpoint}`, queryParams);
        
        const response = await apiClient.get(`/reports/indicadores/${endpoint}`, {
          params: queryParams
        });
        
        console.log(`✅ Relatório ${endpoint} carregado:`, response.data.data);
        return response.data.data;
      } catch (error) {
        return handleReportError(endpoint, error, []);
      }
    },
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
    ...options
  });
};

// 🔥 Hooks específicos para cada indicador
export const useFaturamentoReport = (filters = {}) => 
  useGenericReport('faturamento', filters);

export const useClientesReport = (filters = {}) => 
  useGenericReport('clientes', filters);

// ✅ CORREÇÃO: Hook específico para agendamentos
export const useAgendamentosReport = (filters = {}) => {
  const { user, scope } = useAuth();
  const queryParams = cleanAndFormatFilters(filters);
  // O backend (getAccountFranchiseIds) já infere o tenant certo pelo JWT —
  // essa página nunca chegou a ler um `account` de query param, então
  // forçá-lo aqui só quebrava o relatório pra franquia_admin (que não tem
  // `.account`) sem ganhar nada em troca. Matriz (sem account/franquia)
  // também é um caso válido — o backend agrega globalmente pra ela.
  const tenantId = user?.account?._id || user?.franquia?._id;
  const isMatriz = scope === 'matriz';

  return useQuery({
    queryKey: [REPORTS_QUERY_KEY, 'indicadores', 'agendamentos', queryParams, tenantId, isMatriz],
    queryFn: async () => {
      try {
        logReport('Agendamentos', queryParams);

        const response = await apiClient.get('/reports/indicadores/agendamentos', {
          params: queryParams
        });

        console.log('✅ Relatório de agendamentos carregado:', response.data.data);
        return response.data.data;
      } catch (error) {
        console.error('❌ Erro detalhado no relatório de agendamentos:', {
          message: error.message,
          status: error.response?.status,
          data: error.response?.data
        });

        return handleReportError('agendamentos', error, {
          resumoStatus: [],
          evolucaoDiaria: [],
          porOrigem: [],
          porPlataforma: [],
          porTipo: []
        });
      }
    },
    enabled: !!tenantId || isMatriz, // ✅ SÓ EXECUTA SE USUÁRIO TIVER CONTA, FRANQUIA OU FOR MATRIZ
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
  });
};

export const usePagamentosReport = (filters = {}) => 
  useGenericReport('pagamentos', filters);

export const useAniversariantesReport = (filters = {}) => 
  useGenericReport('aniversariantes', filters);

export const useComissoesReport = (filters = {}) =>
  useGenericReport('comissoes', filters);

// ✅ NOVO: detalhamento (drill-down) de um profissional — Unidade/Serviço/
// Recurso/Item → transações. Só dispara quando profissionalId é fornecido
// (abrir o painel de detalhe de uma linha da tabela de Comissões).
export const useComissoesDetalhe = (profissionalId, filters = {}) => {
  const queryParams = cleanAndFormatFilters(filters);

  return useQuery({
    queryKey: [REPORTS_QUERY_KEY, 'indicadores', 'comissoes', 'detalhe', profissionalId, queryParams],
    queryFn: async () => {
      const response = await apiClient.get('/reports/indicadores/comissoes/detalhe', {
        params: { ...queryParams, profissionalId }
      });
      return response.data.data;
    },
    enabled: !!profissionalId,
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
  });
};

// ✅ NOVO: baixa o PDF do relatório de Comissões (mesmo mecanismo de blob
// download já usado em useReportsExport, retargeted pro endpoint de PDF —
// mais o compartilhamento via Web Share API quando o navegador suporta
// arquivo (Chrome/Edge Android; desktop cai direto pro download).
export const useComissoesPdf = () => {
  const baixarPdf = async (filters = {}) => {
    const queryParams = cleanAndFormatFilters(filters);
    const response = await apiClient.get('/reports/indicadores/comissoes/pdf', {
      params: queryParams,
      responseType: 'blob'
    });

    const nomeArquivo = `comissoes-${new Date().toISOString().split('T')[0]}.pdf`;
    const arquivo = new File([response.data], nomeArquivo, { type: 'application/pdf' });

    if (navigator.canShare?.({ files: [arquivo] }) && navigator.share) {
      try {
        await navigator.share({ files: [arquivo], title: 'Relatório de Comissões' });
        return;
      } catch (err) {
        if (err?.name === 'AbortError') return; // usuário cancelou a folha de compartilhamento
        // cai pro download abaixo em qualquer outro erro do Web Share API
      }
    }

    const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', nomeArquivo);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  return { baixarPdf };
};

export const useProdutosReport = (filters = {}) => 
  useGenericReport('produtos', filters);

export const useServicosReport = (filters = {}) => 
  useGenericReport('servicos', filters);

// ===================================================================================
// HOOKS COMBINADOS E ESPECIALIZADOS - CORRIGIDOS
// ===================================================================================

/**
 * Hook completo para dashboard de vendas
 */
export const useVendasDashboard = (filters = {}) => {
  const { data: consolidado, isLoading: loadingConsolidado } = useVendasConsolidado(filters);
  const { data: comissoes, isLoading: loadingComissoes } = useVendasComissoes(filters);
  const { data: produtos, isLoading: loadingProdutos } = useVendasPorProduto(filters);
  const { data: evolucao, isLoading: loadingEvolucao } = useEvolucaoVendas(filters);

  const isLoading = loadingConsolidado || loadingComissoes || loadingProdutos || loadingEvolucao;

  return {
    // Dados principais
    consolidado: consolidado || {},
    comissoes: comissoes || { comissoes: [], estatisticasGerais: {} },
    produtos: produtos || [],
    evolucao: evolucao || [],
    
    // Estados
    isLoading,
    
    // KPIs calculados para dashboard
    kpis: {
      totalVendas: consolidado?.totalVendas || 0,
      totalFaturado: consolidado?.totalFaturado || 0,
      totalPago: consolidado?.totalPago || 0,
      saldoDevedor: consolidado?.saldoDevedor || 0,
      mediaTicket: consolidado?.mediaTicket || 0,
      taxaConversao: consolidado?.taxaConversao || 0,
      totalComissoes: comissoes?.estatisticasGerais?.totalComissoes || 0,
      totalProfissionais: comissoes?.estatisticasGerais?.totalProfissionais || 0
    },
    
    // Dados para gráficos
    graficos: {
      evolucaoVendas: evolucao,
      vendasPorProduto: produtos,
      comissoes: comissoes?.comissoes || []
    }
  };
};

/**
 * Hook completo para relatórios financeiros
 */
export const useFinanceiroReports = (filters = {}) => {
  const { data: faturamento, isLoading: loadingFaturamento } = useFaturamentoReport(filters);
  const { data: pagamentos, isLoading: loadingPagamentos } = usePagamentosReport(filters);
  const { data: vendasConsolidado, isLoading: loadingVendas } = useVendasConsolidado(filters);

  const isLoading = loadingFaturamento || loadingPagamentos || loadingVendas;

  return {
    faturamento: faturamento || {},
    pagamentos: pagamentos || [],
    vendas: vendasConsolidado || {},
    isLoading,
    
    // Métricas financeiras consolidadas
    metrics: {
      totalFaturado: vendasConsolidado?.totalFaturado || 0,
      totalRecebido: vendasConsolidado?.totalPago || 0,
      saldoPendente: vendasConsolidado?.saldoDevedor || 0,
      ticketMedio: vendasConsolidado?.mediaTicket || 0,
      distribuicaoPagamentos: pagamentos
    }
  };
};

/**
 * Hook completo para relatórios de clientes
 */
export const useClientesReports = (filters = {}) => {
  const { data: clientes, isLoading: loadingClientes } = useClientesReport(filters);
  const { data: aniversariantes, isLoading: loadingAniversariantes } = useAniversariantesReport(filters);

  const isLoading = loadingClientes || loadingAniversariantes;

  return {
    clientes: clientes || [],
    aniversariantes: aniversariantes || [],
    isLoading,
    
    // Estatísticas de clientes
    stats: {
      totalClientes: clientes?.length || 0,
      clientesComAgendamentos: clientes?.filter(c => c.totalAgendamentos > 0).length || 0,
      clientesSemAgendamentos: clientes?.filter(c => c.totalAgendamentos === 0).length || 0,
      totalAniversariantes: aniversariantes?.length || 0
    }
  };
};

/**
 * ✅ NOVO: Hook completo para relatórios de agendamentos
 */
export const useAgendamentosReports = (filters = {}) => {
  const { data: agendamentos, isLoading: loadingAgendamentos } = useAgendamentosReport(filters);
  const { data: servicos, isLoading: loadingServicos } = useServicosReport(filters);

  const isLoading = loadingAgendamentos || loadingServicos;

  return {
    agendamentos: agendamentos || {
      resumoStatus: [],
      evolucaoDiaria: [],
      porOrigem: [],
      porPlataforma: [],
      porTipo: []
    },
    servicos: servicos || [],
    isLoading,
    
    // Estatísticas consolidadas
    stats: {
      totalAgendamentos: agendamentos?.resumoStatus?.reduce((sum, item) => sum + item.count, 0) || 0,
      agendamentosPorStatus: agendamentos?.resumoStatus || [],
      agendamentosPorOrigem: agendamentos?.porOrigem || [],
      agendamentosPorPlataforma: agendamentos?.porPlataforma || [],
      evolucaoDiaria: agendamentos?.evolucaoDiaria || []
    }
  };
};

/**
 * Hook para dados completos do dashboard principal
 */
export const useMainDashboard = (filters = {}) => {
  const { data: contaDashboard, isLoading: loadingDashboard } = useContaDashboard(filters);
  const { data: planUsage, isLoading: loadingPlan } = usePlanUsageReport();
  const { kpis: vendasKpis, isLoading: loadingVendas } = useVendasDashboard(filters);

  const isLoading = loadingDashboard || loadingPlan || loadingVendas;

  return {
    // Dados do dashboard principal
    dashboard: contaDashboard || {},
    planUsage: planUsage || {},
    vendas: vendasKpis || {},
    
    // Estados
    isLoading,
    
    // KPIs consolidados para exibição
    consolidatedKpis: {
      // Do dashboard de agendamentos
      totalAgendamentos: contaDashboard?.kpis?.totalAgendamentos || 0,
      faturamentoAgendamentos: contaDashboard?.kpis?.faturamentoTotal || 0,
      novosClientes: contaDashboard?.kpis?.novosClientes || 0,
      
      // Das vendas
      totalVendas: vendasKpis.totalVendas || 0,
      faturamentoVendas: vendasKpis.totalFaturado || 0,
      ticketMedio: vendasKpis.mediaTicket || 0,
      
      // Combinados
      faturamentoTotal: (contaDashboard?.kpis?.faturamentoTotal || 0) + (vendasKpis.totalFaturado || 0)
    },
    
    // Dados para gráficos
    charts: {
      evolucaoDiaria: contaDashboard?.evolucaoDiaria || [],
      evolucaoMensal: contaDashboard?.evolucaoMensal || [],
      evolucaoAnual: contaDashboard?.evolucaoAnual || [],
      evolucaoVendas: vendasKpis.evolucao || []
    }
  };
};

/**
 * Hook para exportação de relatórios
 */
export const useReportsExport = () => {
  const exportReport = async (tipo, filters = {}, format = 'json') => {
    try {
      const queryParams = cleanAndFormatFilters(filters);
      const response = await apiClient.get(`/reports/export/${tipo}`, {
        params: { ...queryParams, format },
        responseType: format === 'csv' ? 'blob' : 'json'
      });

      if (format === 'csv') {
        // Criar download para CSV
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `relatorio-${tipo}-${new Date().toISOString().split('T')[0]}.csv`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      }

      return response.data;
    } catch (error) {
      console.error(`❌ Erro ao exportar relatório ${tipo}:`, error);
      throw error;
    }
  };

  return { exportReport };
};

// 🔥 Hook principal para gerenciar todos os relatórios (ATUALIZADO)
export const useReports = (config = {}) => {
  const {
    dashboardFilters = {},
    vendasFilters = {},
    financeiroFilters = {},
    clientesFilters = {},
    agendamentosFilters = {} // ✅ ADICIONADO
  } = config;

  const mainDashboard = useMainDashboard(dashboardFilters);
  const vendasDashboard = useVendasDashboard(vendasFilters);
  const financeiroReports = useFinanceiroReports(financeiroFilters);
  const clientesReports = useClientesReports(clientesFilters);
  const agendamentosReports = useAgendamentosReports(agendamentosFilters); // ✅ ADICIONADO
  const { exportReport } = useReportsExport();

  const isLoading = mainDashboard.isLoading || vendasDashboard.isLoading || 
                   financeiroReports.isLoading || clientesReports.isLoading ||
                   agendamentosReports.isLoading; // ✅ ADICIONADO

  return {
    // Dados principais
    mainDashboard: mainDashboard.dashboard,
    planUsage: mainDashboard.planUsage,
    vendas: vendasDashboard,
    financeiro: financeiroReports,
    clientes: clientesReports,
    agendamentos: agendamentosReports, // ✅ ADICIONADO
    
    // Estados
    isLoading,
    
    // KPIs consolidados
    kpis: mainDashboard.consolidatedKpis,
    
    // Métodos
    exportReport,
    
    // Refresh functions
    refreshAll: () => {
      console.log('🔄 Atualizando todos os relatórios');
      // Esta função seria usada com queryClient.invalidateQueries no componente
    }
  };
};

export default useReports;